import React, { useEffect, useMemo, useState } from 'react';
import { invoiceService, roomService } from '../services/api';
import { Invoice, Room } from '../types';
import { useAuth } from '../context/AuthContext';
import {
  normalizePaymentType,
  paymentTypeLabel,
  recordMatchesSearch,
  recordRoomLabel,
} from '../utils/paymentDisplay';
import { creatorDisplayName } from '../utils/creatorDisplay';
import { isGuestStayInvoice } from '../utils/guestInvoiceDisplay';
import { toast } from 'react-toastify';
import PhoneLink from '../components/PhoneLink';
import TenantNameLink from '../components/TenantNameLink';
import './Invoices.css';

const Invoices: React.FC = () => {
  const { hostel } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [layoutRooms, setLayoutRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState({
    month: '',
    year: '',
    type: '',
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const roomById = useMemo(
    () => Object.fromEntries(layoutRooms.map((r) => [r.id, r])) as Record<number, Room>,
    [layoutRooms]
  );

  useEffect(() => {
    setLoading(true);
    void loadInvoices();
  }, [hostel?.id]);

  useEffect(() => {
    if (!hostel?.id) {
      setLayoutRooms([]);
      return;
    }
    roomService
      .getRoomsByHostel(hostel.id)
      .then((rooms) => setLayoutRooms(rooms.filter((r) => r.isActive !== false)))
      .catch(() => setLayoutRooms([]));
  }, [hostel?.id]);

  const loadInvoices = async () => {
    try {
      const data = await invoiceService.getAllInvoices();
      setInvoices(data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoice: Invoice) => {
    try {
      const blob = await invoiceService.downloadInvoice(invoice.id, invoice.userId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const filteredInvoices = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = invoices.filter((inv) => {
      if (filter.month && String(inv.paymentMonth ?? '').trim().toLowerCase() !== filter.month.trim().toLowerCase()) {
        return false;
      }
      if (filter.year && String(inv.paymentYear ?? '').trim() !== filter.year.trim()) return false;
      if (filter.type) {
        const want = normalizePaymentType(filter.type);
        if (want === 'GUEST_STAY') {
          if (!isGuestStayInvoice(inv)) return false;
        } else if (normalizePaymentType(inv.paymentType) !== want) {
          return false;
        }
      }
      if (q) {
        const roomLabel = recordRoomLabel(inv.room, roomById);
        if (!recordMatchesSearch(q, { name: inv.name, phone: inv.phoneNumber, room: roomLabel })) return false;
      }
      return true;
    });
    return [...list].sort((a, b) => {
      const ta = new Date(a.paymentTimestamp).getTime();
      const tb = new Date(b.paymentTimestamp).getTime();
      const aTime = Number.isFinite(ta) ? ta : 0;
      const bTime = Number.isFinite(tb) ? tb : 0;
      if (bTime !== aTime) return bTime - aTime;
      return b.id - a.id;
    });
  }, [invoices, filter, query, roomById]);

  useEffect(() => {
    setPage(1);
  }, [query, filter.month, filter.year, filter.type, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredInvoices.slice(start, start + pageSize);
  }, [filteredInvoices, page, pageSize]);

  const pageNumbers = useMemo(() => {
    const max = Math.min(totalPages, 10);
    const nums: number[] = [];
    for (let i = 1; i <= max; i++) nums.push(i);
    return nums;
  }, [totalPages]);

  if (loading) {
    return <div className="loading">Loading invoices...</div>;
  }

  return (
    <div className="invoices-page">
      <header className="invoices-page-header">
        <h1>All Invoices</h1>
      </header>
      <div className="invoices-toolbar">
          <label className="invoices-search-field">
            <span className="invoices-search-icon" aria-hidden>
              🔍
            </span>
            <input
              type="search"
              className="invoices-search-input"
              placeholder="Search by name or room…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search invoices"
            />
          </label>
          <div className="filters">
            <select
              value={filter.month}
              onChange={(e) => setFilter({ ...filter, month: e.target.value })}
            >
              <option value="">All Months</option>
              {['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <select
              value={filter.year}
              onChange={(e) => setFilter({ ...filter, year: e.target.value })}
            >
              <option value="">All Years</option>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y.toString()}>{y}</option>
              ))}
            </select>
            <select
              value={filter.type}
              onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            >
              <option value="">All Types</option>
              <option value="RENT">Rent</option>
              <option value="ADVANCE">Advance</option>
              <option value="REFUND">Refund</option>
              <option value="GUEST_STAY">Guest</option>
            </select>
          </div>
      </div>

      <div className="invoices-table-container responsive-table-wrap">
        <table className="invoices-table responsive-table">
          <thead>
            <tr>
              <th>Invoice ID</th>
              <th>Name</th>
              <th>Room</th>
              <th>Phone</th>
              <th>Month/Year</th>
              <th>Type</th>
              <th>Paid Amount</th>
              <th>Discount</th>
              <th>Balance</th>
              <th>Date</th>
              <th>Created by</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 ? (
              <tr className="responsive-table__empty">
                <td colSpan={12} className="no-data" data-label="">
                  No invoices found
                </td>
              </tr>
            ) : (
              pageItems.map((invoice) => (
                <tr key={invoice.id}>
                  <td data-label="Invoice">#{invoice.id}</td>
                  <td className="responsive-table__primary" data-label="Name">
                    <TenantNameLink userId={invoice.userId} name={invoice.name} />
                  </td>
                  <td data-label="Room">{recordRoomLabel(invoice.room, roomById) || '—'}</td>
                  <td data-label="Phone">
                    <PhoneLink phone={invoice.phoneNumber} />
                  </td>
                  <td data-label="Month/Year">
                    {invoice.paymentMonth}/{invoice.paymentYear}
                  </td>
                  <td data-label="Type">
                    <span
                      className={`type-badge ${
                        isGuestStayInvoice(invoice)
                          ? 'guest_stay'
                          : normalizePaymentType(invoice.paymentType).toLowerCase()
                      }`}
                    >
                      {isGuestStayInvoice(invoice) ? 'Guest' : paymentTypeLabel(invoice.paymentType)}
                    </span>
                  </td>
                  <td data-label="Paid">₹{parseFloat(invoice.paidAmount || '0').toLocaleString()}</td>
                  <td data-label="Discount">
                    ₹{parseFloat(invoice.discount || '0').toLocaleString()}
                  </td>
                  <td data-label="Balance">₹{parseFloat(invoice.balanceAmount || '0').toLocaleString()}</td>
                  <td data-label="Date">{new Date(invoice.paymentTimestamp).toLocaleDateString()}</td>
                  <td className="invoices-col-creator" data-label="Created by" title={creatorDisplayName(invoice)}>
                    {creatorDisplayName(invoice)}
                  </td>
                  <td className="responsive-table__actions" data-label="Actions">
                    <button
                      type="button"
                      onClick={() => handleDownload(invoice)}
                      className="download-btn"
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <footer className="invoices-table-footer">
          <div className="invoices-pagination">
            <button
              type="button"
              className="invoices-page-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              ←
            </button>
            {pageNumbers.map((n) => (
              <button
                key={n}
                type="button"
                className={`invoices-page-btn${n === page ? ' is-active' : ''}`}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              className="invoices-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              →
            </button>
          </div>
          <div className="invoices-footer-meta">
            <span>
              Showing {filteredInvoices.length === 0 ? 0 : (page - 1) * pageSize + 1} to{' '}
              {Math.min(page * pageSize, filteredInvoices.length)} of {filteredInvoices.length} entries
            </span>
            <label className="invoices-page-size">
              Show
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page">
                <option value={8}>8</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>
          </div>
        </footer>
      </div>

    </div>
  );
};

export default Invoices;

